import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take, tap } from 'rxjs/operators';
import { HttpCacheService } from '../services/http-cache.service';
import { environment } from '../../../environments/environment';

@Injectable()
export class HttpCacheInterceptorService implements HttpInterceptor {

  private ignoredUrls: string[] = [
    '/api/validateEmail/',
    '/api/search',
    '/assets/alive.txt'
  ];

  constructor(private httpCache: HttpCacheService) {}

  /**
   * Provides AGGRESSIVE http GET request caching.
   *
   * When the same URL has been requested multiple times in XX seconds (adjustable in cache service),
   * they will share the same request instance instead of doing 3 separate request.
   *
   * Only works with GET requests.
   *
   * @param request
   * @param next
   */
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.method !== 'GET') {
      // Purge the cache if the request is not GET,
      // This is to help us not need to specify manual purging in most areas.
      // Certain areas might need some fine tuning, but this should cover most bases.
      // NOTE: 2025-07-29: This is not good as it turns out.
      // Because if you call a some HTTP call with POST while some other
      // components request the same http call, it will purge the cache for other endpoints
      // resulting in duplicated requests
      // this.httpCache.purge();
      return next.handle(request);
    }

    const ignored = this.ignoredUrls.some(url => request.urlWithParams.startsWith(url));
    if (ignored) {
      return next.handle(request);
    }

    // Check if we have it in the ignore once list.
    if (this.httpCache.hasIgnoreOnce(request.urlWithParams)) {
      return next.handle(request);
    }

    // Do cleanup first
    this.httpCache.cleanup();

    // Set urls and get current cache
    const origRequestUrl = request.urlWithParams;
    let existingCache = this.httpCache.get(origRequestUrl);

    if (existingCache) {
      return existingCache
        .request
        .pipe(
          filter(val => val !== null),
          take(1),
          switchMap((val) => {
            if (val instanceof HttpErrorResponse) {
              return throwError(HttpErrorResponse);
            } else {
              const response = val as HttpResponse<any>;

              // Send cache hit to notifier, for debugging
              this.notifyCacheHit(origRequestUrl);

              // Make sure to always clone - as it can be mutated by the consumers
              return of(response.clone());
            }
          }),
        );
    } else {
      // Add it to the cache
      // console.log('Adding entries with TTL:', this.httpCache.currentTTL);
      this.httpCache.add(request.urlWithParams);
      existingCache = this.httpCache.get(origRequestUrl);
    }

    return next.handle(request)
      .pipe(
        catchError((err) => {
          // We need the cache to complete the request errors out
          // If we don't do this, the request will never be made again to the same URL
          return throwError(err);
        }),
        tap((event) => {
          if (event instanceof HttpResponse || event instanceof HttpErrorResponse) {
            existingCache.request.next(event);
          }
        }),
        finalize(() => {
          existingCache.completed = true;
          this.httpCache.cleanupIgnoreOnce(request.urlWithParams);
        })
      );
  }

  private notifyCacheHit(url: string): void {
    let enabled = false;
    try {
      // On production and test servers, set this value in local storage via chrome dev tools to debug http cache hits.
      // This will help in identifying http-cache related issues faster.
      enabled = window.localStorage.getItem('townip_http_cache_debug') === 'true'
        || environment.production === false;
    } catch (e) {
      // Local storage not supported...
      // Or values not existing.
      // Fail silently because this is more of a debug thing.
    }

    if (!enabled) {
      return;
    }

    console.warn(`[${new Date().toISOString()}] Cache hit: ${url}`);
  }

}
